<template>
  <div id="AdminRegistryDevices" class="container">
    <div class="row justify-content-between align-items-center mb-3 mt-4">
      <div class="col-auto">
        <h1>Registry Devices</h1>
      </div>
      <div class="col-12 mb-3">
        <div class="alert alert-info text-dark">
          <strong>Search fields:</strong> ID, Device EIC, Device Name, Company
          Name
        </div>
      </div>
      <div class="col-auto">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            placeholder="Search registry devices..."
            v-model="searchQuery"
            @input="debouncedSearch"
          />
          <select
            class="form-select"
            style="max-width: 200px"
            v-model="selectedCountry"
            @change="handleSearch"
          >
            <option value="">All Countries</option>
            <option
              v-for="country in SUPPORTED_COUNTRIES_WITH_LABELS"
              :key="country.value"
              :value="country.value"
            >
              {{ country.label }}
            </option>
          </select>
          <div class="input-group-text">
            <div class="form-check mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                id="showDeleted"
                v-model="showDeleted"
                @change="handleSearch"
              />
              <label class="form-check-label" for="showDeleted">
                Show Deleted
              </label>
            </div>
          </div>
          <button
            class="btn btn-outline-secondary"
            type="button"
            @click="toggleColumnSelector"
          >
            Columns
          </button>
        </div>
      </div>
    </div>

    <!-- Column Selector Modal -->
    <div
      class="modal fade"
      id="columnSelectorModal"
      tabindex="-1"
      ref="columnModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Select Columns</h5>
            <button
              type="button"
              class="btn-close"
              @click="toggleColumnSelector"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6>Registry Device Fields</h6>
                <div
                  class="form-check"
                  v-for="field in registryDeviceFields"
                  :key="field"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="field"
                    v-model="selectedColumns.registryDevice[field]"
                  />
                  <label class="form-check-label" :for="field">{{
                    field
                  }}</label>
                </div>
              </div>
              <div class="col-md-6">
                <h6>Company Fields</h6>
                <div
                  class="form-check"
                  v-for="field in companyFields"
                  :key="field"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'company_' + field"
                    v-model="selectedColumns.company[field]"
                  />
                  <label class="form-check-label" :for="'company_' + field">{{
                    field
                  }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Registry Devices Table -->
    <div class="table-container table-responsive scrollbar">
      <table class="table table-hover align-middle">
        <thead>
          <tr>
            <template v-for="field in registryDeviceFields" :key="field">
              <th
                v-if="selectedColumns.registryDevice[field]"
                @click="handleSort(field)"
                class="sortable"
              >
                {{ field }}
                <span v-if="sortBy === field" class="ms-1">
                  {{ sortOrder === "desc" ? "▼" : "▲" }}
                </span>
              </th>
            </template>
            <template v-for="field in companyFields" :key="field">
              <th
                v-if="selectedColumns.company[field]"
                @click="handleSort(`company.${field}`)"
                class="sortable"
              >
                Company {{ field }}
                <span v-if="sortBy === `company.${field}`" class="ms-1">
                  {{ sortOrder === "desc" ? "▼" : "▲" }}
                </span>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="device in registryDevices" :key="device.id">
            <template v-for="field in registryDeviceFields" :key="field">
              <td v-if="selectedColumns.registryDevice[field]">
                {{ formatField(device[field]) }}
              </td>
            </template>
            <template v-for="field in companyFields" :key="field">
              <td v-if="selectedColumns.company[field]">
                {{ formatField(device.company?.[field]) }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div class="d-flex align-items-center">
        <label class="me-2">Per page:</label>
        <select
          v-model="perPage"
          class="form-select form-select-sm"
          style="width: 80px"
          @change="handlePageChange(1)"
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="1000">1000</option>
        </select>
      </div>
      <nav aria-label="Registry device pagination">
        <ul class="pagination mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a
              class="page-link"
              href="#"
              @click.prevent="handlePageChange(currentPage - 1)"
              >Previous</a
            >
          </li>
          <li
            v-for="p in paginationRange"
            :key="p"
            class="page-item"
            :class="{ active: currentPage === p }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="handlePageChange(p)"
              >{{ p }}</a
            >
          </li>
          <li
            class="page-item"
            :class="{ disabled: currentPage === totalPages }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="handlePageChange(currentPage + 1)"
              >Next</a
            >
          </li>
        </ul>
      </nav>
      <div class="text-muted">Total: {{ totalItems }} devices</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { goSolidApi } from "@/composables/useGoSolidApi";
import { SUPPORTED_COUNTRIES_WITH_LABELS } from "@/constants";
import { Modal } from "bootstrap";
import { debounce } from "lodash-es";
import { computed, onMounted, ref, useTemplateRef } from "vue";

interface RegistryDevice {
  id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  device_eic: string;
  device_name: string;
  device_power: number;
  support: boolean;
  registry_status: string;
  technology_code: string;
  commercial_operation_date: string | null;
  registry_metadata: any;
  projection_multiplier: number | null;
  company?: Company;
  [key: string]: string | number | boolean | Company | any | null;
}

interface Company {
  id: number;
  name: string;
  registry_code: string;
  has_registered: boolean;
  country: string;
  [key: string]: string | number | boolean;
}

const registryDevices = ref<RegistryDevice[]>([]);
const searchQuery = ref("");
const selectedCountry = ref("");
const columnModal = useTemplateRef<HTMLElement>("columnModal");
let bsModal: Modal | null = null;
const currentPage = ref(1);
const perPage = ref(25);
const totalItems = ref(0);
const totalPages = ref(0);
const sortBy = ref<string | null>(null);
const sortOrder = ref<"asc" | "desc">("desc");
const showDeleted = ref(false);

// Define available fields for each model
const registryDeviceFields = [
  "id",
  "created_at",
  "updated_at",
  "deleted_at",
  "device_eic",
  "device_name",
  "device_power",
  "support",
  "registry_status",
  "technology_code",
  "commercial_operation_date",
  "registry_metadata",
  "projection_multiplier",
  "integration_name",
];

const companyFields = [
  "id",
  "name",
  "registry_code",
  "has_registered",
  "country",
];

// Initialize selected columns from localStorage or defaults
const selectedColumns = ref({
  registryDevice: JSON.parse(
    localStorage.getItem("admin_registry_devices_columns_device") || "{}"
  ),
  company: JSON.parse(
    localStorage.getItem("admin_registry_devices_columns_company") || "{}"
  ),
});

// Set default selections if none exist
if (Object.keys(selectedColumns.value.registryDevice).length === 0) {
  selectedColumns.value.registryDevice = {
    id: true,
    device_eic: true,
    device_name: true,
    device_power: true,
    support: true,
    technology_code: true,
    registry_status: true,
    commercial_operation_date: true,
  };
}
if (Object.keys(selectedColumns.value.company).length === 0) {
  selectedColumns.value.company = {
    id: true,
    name: true,
    country: true,
  };
}

const saveColumnSelections = () => {
  localStorage.setItem(
    "admin_registry_devices_columns_device",
    JSON.stringify(selectedColumns.value.registryDevice)
  );
  localStorage.setItem(
    "admin_registry_devices_columns_company",
    JSON.stringify(selectedColumns.value.company)
  );
};

// Format field values for display
const formatField = (value: any): string => {
  if (value === null || value === undefined) return "-";
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

// Computed property for pagination range
const paginationRange = computed(() => {
  const range = [];
  const maxButtons = 5;
  const start = Math.max(1, currentPage.value - Math.floor(maxButtons / 2));
  const end = Math.min(totalPages.value, start + maxButtons - 1);

  for (let i = start; i <= end; i++) {
    range.push(i);
  }

  return range;
});

const fetchRegistryDevices = async (search = "") => {
  try {
    const response = await goSolidApi.get("/api/admin/registry-devices", {
      params: {
        search,
        country: selectedCountry.value,
        page: currentPage.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        sort_order: sortOrder.value,
        show_deleted: showDeleted.value,
      },
    });
    registryDevices.value = response.data.data;
    totalItems.value = response.data.pagination.total;
    totalPages.value = response.data.pagination.total_pages;

    // Ensure current page is valid
    if (currentPage.value > totalPages.value) {
      handlePageChange(1);
    }
  } catch (error) {
    console.error("Error fetching registry devices:", error);
  }
};

// Handle page changes
const handlePageChange = (page: number) => {
  currentPage.value = page;
  handleSearch();
};

const handleSearch = () => {
  fetchRegistryDevices(searchQuery.value);
};

// Create a debounced search function
const debouncedSearch = debounce(handleSearch, 500);

// Column selector modal toggle
const toggleColumnSelector = () => {
  if (!bsModal) {
    bsModal = new Modal(columnModal.value as HTMLElement);
  }
  bsModal.toggle();
  saveColumnSelections();
};

// Handle sorting
const handleSort = (field: string) => {
  if (sortBy.value === field) {
    // Toggle sort order if clicking the same field
    sortOrder.value = sortOrder.value === "desc" ? "asc" : "desc";
  } else {
    // Set new sort field and default to descending
    sortBy.value = field;
    sortOrder.value = "desc";
  }
  handleSearch();
};

onMounted(() => {
  handleSearch();
});
</script>

<style scoped lang="scss">
#AdminRegistryDevices {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}

.table-container {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
  margin-top: 1rem;
}

.table {
  margin-bottom: 0;
}

.table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.form-check {
  margin-bottom: 0.5rem;
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  padding: 0.375rem 0.75rem;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: var(--phoenix-gray-100);
}

.form-check-label {
  font-size: 0.875rem;
  white-space: nowrap;
}

.input-group-text {
  background-color: transparent;
}

.form-check-input:checked {
  background-color: var(--phoenix-primary);
  border-color: var(--phoenix-primary);
}
</style>
